import React from 'react';
import cn from 'classnames';
import FinalForm, { Field as BaseField } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Label, Input } from '@economic/gaudi';
import CheckboxField from './fields/FieldCheckbox';
import ComboboxField from './fields/FieldCombobox';
import DatePickerField from './fields/FieldDatePicker';
import DropdownField from './fields/FieldDropdown';
import TextField from './fields/FieldText';
import { RadioGroupField, NumberRadioGroupField } from './fields/FieldRadioGroup';
import NumberField from './fields/FieldNumber';
import FragmentField from './fields/FieldFragment';
import { t } from '../l10n/l10n';
import { useFormFieldRegistration } from './Form';
import FieldTextArea from './fields/TextArea';

export interface FieldProps<T> extends FinalForm.FieldProps<any, any> {
    as?: React.ComponentType<T> | 'input';
    fieldClassName?: string;
    label?: React.ReactNode;
    required?: boolean;
}

const Field = ({ as, component: _, disabled, fieldClassName, label, required, supplementary, ...props }: FieldProps<any>) => {
    const Component = as || Input;
    const registerField: any = useFormFieldRegistration();

    React.useEffect(() => {
        registerField(props.name, label);
    }, []);

    let validate;

    if (required) {
        validate = async (value, allValues) => {
            if (props.validate) {
                const validation = await props.validate(value, allValues);

                if (validation) {
                    return validation;
                }
            }

            return value === undefined || value === null ? t`FORM_FIELD_LABEL_REQUIRED` : undefined;
        };
    } else {
        validate = props.validate;
    }

    return (
        <div className={cn('field', fieldClassName)}>
            <BaseField
                render={({ input, meta, summary, ...componentProps }: any) => {
                    const error = meta.error || meta.submitError;

                    const output = (
                        <Label
                            disabled={componentProps.disabled}
                            state={meta.touched && error ? 'error' : componentProps.message ? 'information' : undefined}
                            message={meta.touched && error ? error : !meta.touched ? componentProps.message : undefined}>
                            {label && (
                                <>
                                    {label}
                                    {required && ' *'}
                                </>
                            )}
                            <Component {...input} {...componentProps} state={meta.touched && error ? 'error' : null} />
                        </Label>
                    );

                    return supplementary ? (
                        <div className="relative">
                            {output}
                            {supplementary && (
                                <div className="field__label__container__supplementary__field absolute top-0 right-0 -mt-1">
                                    {supplementary}
                                </div>
                            )}
                        </div>
                    ) : (
                        output
                    );
                }}
                parse={value => (value === null ? undefined : value)}
                {...props}
                disabled={disabled}
                validate={validate}
            />
        </div>
    );
};

Field.Checkbox = CheckboxField;
Field.Combobox = ComboboxField;
Field.DatePicker = DatePickerField;
Field.Dropdown = DropdownField;
Field.Text = TextField;
Field.RadioGroup = RadioGroupField;
Field.NumberRadioGroup = NumberRadioGroupField;
Field.Number = NumberField;
Field.Fragment = FragmentField;
Field.Base = BaseField;
Field.Hidden = props => <BaseField {...props} component="input" type="hidden" />;
Field.Array = FieldArray;
Field.TextArea = FieldTextArea;

export default Field;
